import sumBy from 'lodash/sumBy';
import type { OnlineApplicationOrder } from '../../../Api/OnlineApplicationForm/Orders/OnlineApplicationOrder.csharp';
import { Page } from '../../../ApplicationApplets/Online/OnlineApplicationForm/useApplicationFormFlow';
import { OnlineProgress } from '../../../ApplicationApplets/Shared/FormProgressBar/Online/OnlineProgress';
import ExecutionContext from '../../../ExecutionContext';
import type { OnlineApplicationChoice } from '../../../OnlineApplication/Model';
import type { GoogleTagManagerEcommerceProduct } from '../GoogleTagManagerModels';
import { GtmCommerceEventsHandler } from '../GtmCommerceEventsHandler';
import type { OrderedSubjectItem } from '~/Features/Api/OnlineApplicationForm/Orders/OrderedSubjectItem.csharp';
import { getRawApplicationFormState } from '~/Features/ApplicationApplets/Online/OnlineApplicationForm/FormProgressState/ApplicationFormProgressStateManager';
import { getContentLocale } from '~/Features/Configuration/Utils/languageUtils';
import type { SelectedSubject } from '~/Features/StudyPage/Online/Components/Planner/Context/types';
import { OnlineStudyBrand, SchoolName, SingleSubject } from '~/Partials/GoogleTagManager/AnalyticsConstants';
import { TelemetryLogger } from '~/Utils/Logging/TelemetryLogger';

export class OnlineAnalytics {
    public static addOnlinePlannerProducts = (items: SelectedSubject[]) => {
        const products = OnlineAnalytics.createProductsFromOnlinePlanner(items);
        const actionField = OnlineAnalytics.createBaseActionFieldFromSelectedSubject(items);

        if (products.length > 0) {
            GtmCommerceEventsHandler.pushAddProducts(products, actionField);
        }
    };

    public static removeOnlinePlannerProducts = (items: SelectedSubject[]) => {
        const products = OnlineAnalytics.createProductsFromOnlinePlanner(items);
        const actionField = OnlineAnalytics.createBaseActionFieldFromSelectedSubject(items);

        if (products.length > 0) {
            GtmCommerceEventsHandler.pushRemoveProducts(products, actionField);
        }
    };

    public static addProducts = (items: OnlineApplicationChoice[]) => {
        const products = OnlineAnalytics.createProductsFromOnlineChoice(items);
        const actionField = OnlineAnalytics.createBaseActionFieldFromOnlineApplicationChoice(items);

        if (products.length > 0) {
            GtmCommerceEventsHandler.pushAddProducts(products, actionField);
        }
    };

    public static removeLineProducts = (items: OrderedSubjectItem[], parentStudyCode: string) => {
        const products = OnlineAnalytics.createProductsFromApplication(items, parentStudyCode);
        const actionField = OnlineAnalytics.createBaseActionFieldFromOrderedSubjectItem(items);

        GtmCommerceEventsHandler.pushRemoveProducts(products, actionField);
    };

    public static removeChoiceProducts = (choices: OnlineApplicationChoice[]) => {
        const products = OnlineAnalytics.createProductsFromOnlineChoice(choices);
        const actionField = OnlineAnalytics.createBaseActionFieldFromOnlineApplicationChoice(choices);
        GtmCommerceEventsHandler.pushRemoveProducts(products, actionField);
    };

    public static pushCheckout = (currentPage: Page, order: OnlineApplicationOrder) => {
        const stepNumber = OnlineAnalytics.getStepNumber(currentPage);
        const products = OnlineAnalytics.createProductsFromApplication(
            order.orderedSubjectItems,
            order.parentStudyCode
        );
        const baseActionField = OnlineAnalytics.createBaseActionFieldFromApplication(order);

        if (products.length > 0 && stepNumber) {
            if (stepNumber === 1) {
                GtmCommerceEventsHandler.pushBeginCheckout(products, baseActionField);
            } else {
                const checkoutActionField = {
                    ...baseActionField,
                    checkout_step: stepNumber,
                };
                GtmCommerceEventsHandler.pushCheckout(products, checkoutActionField);
            }

            if (currentPage === Page.Confirmation) {
                if (!ExecutionContext.IsServerSide()) {
                    history.pushState({}, '', 'confirmation/');
                }

                const purchaseActionField = OnlineAnalytics.createPurchaseActionFieldFromApplication(order);
                GtmCommerceEventsHandler.pushPurchase(products, purchaseActionField);
            }
        }
    };

    public static checkoutOption = (currentPage: Page, option: string) => {
        const stepNumber = OnlineAnalytics.getStepNumber(currentPage);
        GtmCommerceEventsHandler.pushCheckoutOption(stepNumber, option);
    };

    private static createProductsFromOnlinePlanner = (items: SelectedSubject[]): GoogleTagManagerEcommerceProduct[] => {
        const resolveCategory = (item: SelectedSubject) => {
            if (item.electiveGroupCode) {
                return item.electiveGroupCode;
            }

            if (item.tab === 'fullTime') {
                return item.parentStudies?.fullTimeCode;
            }

            return item.parentStudies?.partTimeCode;
        };

        return items.map(item => {
            if (!item.educationCategories) {
                TelemetryLogger.errorMessage(
                    `Missing education categories for subject ${item.subjectCode} [${this.getStateVersion()}]`
                );
                item.educationCategories = [];
            }

            return {
                item_id: item.subjectCode,
                item_name: item.subjectName,
                price: item.price.amount,
                affiliation: OnlineStudyBrand,
                item_brand: item.educationCategories.join(', '),
                item_category: resolveCategory(item) ?? '',
                item_category2: item.points.amount.toLocaleString(getContentLocale()),
                item_category3: SchoolName,
                item_variant: SingleSubject,
                quantity: 1,
            };
        }) satisfies GoogleTagManagerEcommerceProduct[];
    };

    private static createProductsFromApplication = (items: OrderedSubjectItem[], parentStudyCode: string) =>
        items.map(item => {
            if (!item.subject.educationCategories) {
                TelemetryLogger.errorMessage(
                    `Missing education categories for subject ${item.subjectCode} [${this.getStateVersion()}]`
                );
                item.subject.educationCategories = [];
            }

            return {
                item_id: item.subjectCode,
                item_name: item.subjectName,
                price: item.subject.price.amount,
                affiliation: OnlineStudyBrand,
                item_brand: item.subject.educationCategories.join(', '),
                item_category: parentStudyCode,
                item_category2: item.subjectCredits.toLocaleString(getContentLocale()),
                item_category3: SchoolName,
                item_variant: SingleSubject,
                quantity: 1,
            };
        }) satisfies GoogleTagManagerEcommerceProduct[];

    private static createProductsFromOnlineChoice = (choices: OnlineApplicationChoice[]) =>
        choices.map(choice => {
            if (!choice.subject.educationCategories) {
                TelemetryLogger.errorMessage(
                    `Missing education categories for subject ${choice.subject.code} [${this.getStateVersion()}]`
                );
                choice.subject.educationCategories = [];
            }

            return {
                item_id: choice.subject.code,
                item_name: choice.subject.name,
                price: choice.subject.price.amount,
                affiliation: OnlineStudyBrand,
                item_brand: choice.subject.educationCategories.join(', '),
                item_category: choice.parentStudy?.code ?? '',
                item_category2: choice.subject.credits.toLocaleString(getContentLocale()),
                item_category3: SchoolName,
                item_variant: SingleSubject,
                quantity: 1,
            };
        }) satisfies GoogleTagManagerEcommerceProduct[];

    private static createBaseActionFieldFromApplication = (order: OnlineApplicationOrder) => ({
        currency: order.totalPrice?.currencyCode ?? '',
        value: order.totalPrice ? order.totalPrice.amount : 0,
    });

    private static createPurchaseActionFieldFromApplication = (order: OnlineApplicationOrder) => ({
        ...OnlineAnalytics.createBaseActionFieldFromApplication(order),
        transaction_id: order.orderReference ?? '',
        coupon: order.discountCode,
        shipping: 0,
        tax: 0,
    });

    private static getStepNumber = (activePage: Page) => OnlineProgress.getActiveStepIndex(activePage) + 1;

    private static createBaseActionFieldFromSelectedSubject = (items: SelectedSubject[]) => ({
        currency: items[0].price.currencyCode,
        value: sumBy(items, item => item.price.amount),
    });

    private static createBaseActionFieldFromOnlineApplicationChoice = (items: OnlineApplicationChoice[]) => ({
        currency: items[0].subject.price.currencyCode,
        value: sumBy(items, item => item.subject.price.amount),
    });

    private static createBaseActionFieldFromOrderedSubjectItem = (items: OrderedSubjectItem[]) => ({
        currency: items[0].subject.price.currencyCode,
        value: sumBy(items, item => item.subject.price.amount),
    });

    private static getStateVersion = () => getRawApplicationFormState()?.version;
}
